/**
 * Personal vs role notifications.
 *
 * Every notification row has exactly one recipient (`infinityCode`), so
 * "addressed to me" cannot come from the recipient alone. What differs is the
 * capacity the recipient was notified in: about their own requests, pay,
 * account, tasks or conversations (personal), or because they approve,
 * process or review something for others (role). The `type` decides that, and
 * for the types that serve both audiences, so does the affected employee the
 * producer embeds in `actionUrl` (`employee=<code>`).
 *
 * Independent of the active View and of click navigation
 * (constant/notificationRoutes.js).
 */
import { parseActionUrl } from "../constant/notificationRoutes";

const PERSONAL_TYPES = new Set([
  // The recipient's own leave / advancement / complaint.
  "LEAVE_CONFIRMATION",
  "LEAVE_STATUS_UPDATE",
  "LEAVE_APPROVED",
  "LEAVE_REJECTED",
  "MANUAL_LEAVE_ASSIGNED",
  "ADVANCEMENT_REQUEST",
  "ADVANCEMENT_STATUS",
  "ADVANCEMENT_APPROVED",
  "ADVANCEMENT_REJECTED",
  "ADVANCEMENT_DISBURSED",
  "ESCALATION_SUBMITTED",
  "ESCALATION_ASSIGNED",
  "ESCALATION_STATUS_UPDATE",
  // Sent directly to the recipient by someone.
  "MESSAGE",
  "GROUP_ADDED",
  "GROUP_MEMBER_ADDED",
  "CALENDAR_EVENT",
  "TASK_ASSIGNED",
  "TASK_UPDATED",
  "TASK_CANCELLED",
  "REPORT_SHARED",
  // The recipient's own account, pay and attendance.
  "WELCOME",
  "PASSWORD_CHANGED",
  "ADMIN_LEVEL_GRANTED",
  "PERMISSION_UPDATED",
  "PAYROLL_ADJUSTMENT",
  "PAYROLL_PAID",
  "ATTENDANCE_RESET",
]);

// About one specific employee: personal only when that employee is the recipient.
const ABOUT_EMPLOYEE_TYPES = new Set(["EMPLOYEE_ACTION", "EMPLOYEE_CHANGE", "PROFILE_UPDATED", "DOCUMENT_REQUEST"]);

/**
 * @param {object} notification - a notification row (as the API / socket delivers it)
 * @returns {boolean}
 */
export function isPersonalNotification(notification) {
  const type = notification?.type || "";
  if (PERSONAL_TYPES.has(type)) return true;
  if (!ABOUT_EMPLOYEE_TYPES.has(type) || notification.infinityCode == null) return false;

  const recipient = String(notification.infinityCode);
  const { employee } = parseActionUrl(notification.actionUrl);
  if (employee) return String(employee) === recipient;

  // Document rows stored before the requester was embedded: only the
  // requester's own submission confirmation (sent from themselves) is certain.
  return type === "DOCUMENT_REQUEST" && String(notification.from) === recipient;
}
